import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Snackbar,
    Alert,
    Chip,
    IconButton,
    Paper,
    Divider,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    LinearProgress,
    Menu,
    ListItemText,
    Tooltip,
    Grid,
    Slider,
    Autocomplete,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FlagIcon from "@mui/icons-material/Flag";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import TableChartIcon from "@mui/icons-material/TableChart";
import InsightsIcon from "@mui/icons-material/Insights";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { alpha } from "@mui/material/styles";
import { API_URL, apiFetch } from "../config";
import {
    ROADMAP_PILLARS,
    STATUSES,
    EFFORT_BANDS,
    RISK_LEVELS,
    HEALTH_STATES,
    ROADMAP_TEMPLATES,
} from "./roadmap/constants";
import {
    pillarLabel,
    horizonLabel,
    quarterFromDate,
    quarterLabel,
    defaultTargetDate,
    datePlusDays,
    parseTags,
    formatTags,
    roadmapItemsToCsv,
    downloadCsv,
    yearFromDate,
} from "./roadmap/utils";

export { ROADMAP_PILLARS } from "./roadmap/constants";

function TabPanel({ children, value, index }) {
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function statusChipColor(s) {
    switch (s) {
        case "achieved":
            return "success";
        case "in_progress":
            return "primary";
        case "deferred":
            return "default";
        default:
            return "info";
    }
}

function healthChipColor(h) {
    switch (h) {
        case "blocked":
            return "error";
        case "watch":
            return "warning";
        default:
            return "success";
    }
}

const emptyForm = {
    id: null,
    title: "",
    description: "",
    target_date: defaultTargetDate(),
    pillar: "growth",
    status: "planned",
    sort_order: 0,
    owner: "",
    key_metric: "",
    budget_estimate: "",
    effort_band: "m",
    risk_level: "medium",
    health: "on_track",
    progress_percent: 0,
    tags: "",
    success_criteria: "",
    dependency_note: "",
    review_date: "",
};

const Roadmap = () => {
    const [items, setItems] = useState([]);
    const [filterPillar, setFilterPillar] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterRisk, setFilterRisk] = useState("");
    const [filterHealth, setFilterHealth] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [view, setView] = useState("timeline");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTab, setDialogTab] = useState(0);
    const [form, setForm] = useState(emptyForm);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [templateAnchor, setTemplateAnchor] = useState(null);
    const [tableOrder, setTableOrder] = useState("asc");
    const [tableOrderBy, setTableOrderBy] = useState("target_date");

    useEffect(() => {
        const t = setTimeout(() => setSearchQ(searchInput.trim()), 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    const load = useCallback(async () => {
        try {
            const q = new URLSearchParams();
            if (filterPillar) q.set("pillar", filterPillar);
            if (filterStatus) q.set("status", filterStatus);
            if (filterRisk) q.set("risk_level", filterRisk);
            if (filterHealth) q.set("health", filterHealth);
            if (searchQ) q.set("q", searchQ);
            const res = await apiFetch(`${API_URL}/roadmap-items?${q.toString()}`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to load roadmap");
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    }, [filterPillar, filterStatus, filterRisk, filterHealth, searchQ]);

    useEffect(() => {
        load();
    }, [load]);

    const yearOptions = useMemo(() => {
        const y = new Set();
        y.add(new Date().getFullYear());
        y.add(new Date().getFullYear() + 1);
        items.forEach((i) => y.add(yearFromDate(i.target_date)));
        return Array.from(y).sort((a, b) => a - b);
    }, [items]);

    const displayed = useMemo(() => {
        if (!filterYear) return items;
        return items.filter((i) => i.target_date.startsWith(String(filterYear)));
    }, [items, filterYear]);

    const stats = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        const active = items.filter((i) => i.status !== "achieved" && i.status !== "deferred");
        const next = active
            .filter((i) => i.target_date >= today)
            .sort((a, b) => a.target_date.localeCompare(b.target_date))[0];
        const overdue = active.filter((i) => i.target_date < today);
        const blocked = active.filter((i) => i.health === "blocked");
        const watch = active.filter((i) => i.health === "watch");
        const highRisk = active.filter((i) => i.risk_level === "high");
        return {
            total: items.length,
            inProgress: items.filter((i) => i.status === "in_progress").length,
            achieved: items.filter((i) => i.status === "achieved").length,
            next,
            overdueCount: overdue.length,
            blockedCount: blocked.length,
            watchCount: watch.length,
            highRiskActive: highRisk.length,
        };
    }, [items]);

    const pillarMix = useMemo(() => {
        const active = items.filter((i) => i.status !== "achieved" && i.status !== "deferred");
        const counts = {};
        ROADMAP_PILLARS.forEach((p) => {
            counts[p.id] = 0;
        });
        active.forEach((i) => {
            if (counts[i.pillar] != null) counts[i.pillar] += 1;
        });
        const total = active.length || 1;
        return ROADMAP_PILLARS.map((p) => ({
            ...p,
            count: counts[p.id],
            pct: Math.round((counts[p.id] / total) * 100),
        }));
    }, [items]);

    const attentionItems = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return items.filter((i) => {
            if (i.status === "achieved" || i.status === "deferred") return false;
            if (i.health === "blocked") return true;
            if (i.target_date < today && (i.health === "watch" || i.risk_level === "high")) return true;
            return false;
        });
    }, [items]);

    const quarterBuckets = useMemo(() => {
        const y = filterYear ? Number(filterYear) : new Date().getFullYear();
        const buckets = { 1: [], 2: [], 3: [], 4: [] };
        displayed.forEach((item) => {
            if (yearFromDate(item.target_date) !== y) return;
            const q = quarterFromDate(item.target_date);
            if (buckets[q]) buckets[q].push(item);
        });
        return buckets;
    }, [displayed, filterYear]);

    const sortedTableRows = useMemo(() => {
        const cmp = (a, b) => {
            let va = a[tableOrderBy];
            let vb = b[tableOrderBy];
            if (tableOrderBy === "progress_percent") {
                va = Number(va) || 0;
                vb = Number(vb) || 0;
            } else if (tableOrderBy === "budget_estimate") {
                va = va == null ? -1 : Number(va);
                vb = vb == null ? -1 : Number(vb);
            } else {
                va = va != null ? String(va) : "";
                vb = vb != null ? String(vb) : "";
            }
            if (va < vb) return tableOrder === "asc" ? -1 : 1;
            if (va > vb) return tableOrder === "asc" ? 1 : -1;
            return 0;
        };
        return [...displayed].sort(cmp);
    }, [displayed, tableOrder, tableOrderBy]);

    const handleTableSort = (prop) => {
        const isAsc = tableOrderBy === prop && tableOrder === "asc";
        setTableOrder(isAsc ? "desc" : "asc");
        setTableOrderBy(prop);
    };

    const openAdd = () => {
        setForm({ ...emptyForm, target_date: defaultTargetDate() });
        setDialogTab(0);
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setForm({
            id: row.id,
            title: row.title,
            description: row.description || "",
            target_date: row.target_date,
            pillar: row.pillar || "growth",
            status: row.status || "planned",
            sort_order: row.sort_order ?? 0,
            owner: row.owner || "",
            key_metric: row.key_metric || "",
            budget_estimate: row.budget_estimate != null ? String(row.budget_estimate) : "",
            effort_band: row.effort_band || "m",
            risk_level: row.risk_level || "medium",
            health: row.health || "on_track",
            progress_percent: Number(row.progress_percent) || 0,
            tags: row.tags || "",
            success_criteria: row.success_criteria || "",
            dependency_note: row.dependency_note || "",
            review_date: row.review_date || "",
        });
        setDialogTab(0);
        setDialogOpen(true);
    };

    const payloadFromForm = () => ({
        title: form.title.trim(),
        description: form.description.trim() || null,
        target_date: form.target_date,
        pillar: form.pillar,
        status: form.status,
        sort_order: Number(form.sort_order) || 0,
        owner: form.owner.trim() || null,
        key_metric: form.key_metric.trim() || null,
        budget_estimate: form.budget_estimate === "" ? null : Number(form.budget_estimate),
        effort_band: form.effort_band,
        risk_level: form.risk_level,
        health: form.health,
        progress_percent: Number(form.progress_percent) || 0,
        tags: form.tags.trim() || null,
        success_criteria: form.success_criteria.trim() || null,
        dependency_note: form.dependency_note.trim() || null,
        review_date: form.review_date || null,
    });

    const save = async () => {
        if (!form.title.trim() || !form.target_date) {
            setSnackbar({ open: true, message: "Title and target date are required.", severity: "warning" });
            return;
        }
        try {
            const body = payloadFromForm();
            const url = form.id ? `${API_URL}/roadmap-items/${form.id}` : `${API_URL}/roadmap-items`;
            const res = await apiFetch(url, {
                method: form.id ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || "Save failed");
            setSnackbar({ open: true, message: form.id ? "Updated." : "Added.", severity: "success" });
            setDialogOpen(false);
            load();
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    };

    const remove = async (id) => {
        try {
            const res = await apiFetch(`${API_URL}/roadmap-items/${id}`, { method: "DELETE" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || "Delete failed");
            setSnackbar({ open: true, message: "Removed.", severity: "success" });
            load();
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    };

    const duplicateItem = async (row) => {
        try {
            const p = {
                title: `${row.title} (copy)`,
                description: row.description || null,
                target_date: row.target_date,
                pillar: row.pillar || "growth",
                status: "planned",
                sort_order: (row.sort_order ?? 0) + 1,
                owner: row.owner || null,
                key_metric: row.key_metric || null,
                budget_estimate: row.budget_estimate != null ? Number(row.budget_estimate) : null,
                effort_band: row.effort_band || "m",
                risk_level: row.risk_level || "medium",
                health: "on_track",
                progress_percent: 0,
                tags: row.tags || null,
                success_criteria: row.success_criteria || null,
                dependency_note: row.dependency_note || null,
                review_date: row.review_date || null,
            };
            const res = await apiFetch(`${API_URL}/roadmap-items`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(p),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || "Duplicate failed");
            setSnackbar({ open: true, message: "Duplicated as a new planned item.", severity: "success" });
            load();
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    };

    const applyTemplate = async (template) => {
        setTemplateAnchor(null);
        let n = 0;
        try {
            for (const it of template.items) {
                const res = await apiFetch(`${API_URL}/roadmap-items`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        title: it.title,
                        description: it.description || null,
                        target_date: datePlusDays(it.daysFromNow ?? 14),
                        pillar: it.pillar || "growth",
                        status: "planned",
                        sort_order: n,
                        effort_band: it.effort_band || "m",
                        risk_level: it.risk_level || "medium",
                        health: "on_track",
                        progress_percent: 0,
                    }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.error || "Template item failed");
                n += 1;
            }
            setSnackbar({ open: true, message: `Added ${n} milestones from “${template.name}”.`, severity: "success" });
            load();
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    };

    const exportCsv = () => {
        const csv = roadmapItemsToCsv(displayed);
        downloadCsv(`roadmap-${filterYear || "all"}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    };

    const tagValues = parseTags(form.tags);

    return (
        <Box>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-start" }} spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em" }}>
                        Strategic roadmap
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                        Plan by quarter and year, track owners, risk, and health. Switch views for timeline planning,
                        annual layout, operational table, or insights.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="outlined" size="small" startIcon={<PlaylistAddIcon />} onClick={(e) => setTemplateAnchor(e.currentTarget)}>
                        Templates
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={exportCsv} disabled={!displayed.length}>
                        Export CSV
                    </Button>
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openAdd}>
                        Add milestone
                    </Button>
                </Stack>
            </Stack>

            <Menu anchorEl={templateAnchor} open={Boolean(templateAnchor)} onClose={() => setTemplateAnchor(null)}>
                {ROADMAP_TEMPLATES.map((t) => (
                    <MenuItem key={t.id} onClick={() => applyTemplate(t)}>
                        <ListItemText primary={t.name} secondary={t.description} />
                    </MenuItem>
                ))}
            </Menu>

            <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ lg: "center" }} flexWrap="wrap" useFlexGap>
                <TextField
                    label="Search"
                    size="small"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Title, owner, metric, tags…"
                    sx={{ minWidth: 220, flex: { xs: 1, lg: "none" } }}
                />
                <TextField label="Year focus" size="small" select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} sx={{ minWidth: 130 }}>
                    <MenuItem value="">All years</MenuItem>
                    {yearOptions.map((y) => (
                        <MenuItem key={y} value={String(y)}>
                            {y}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField label="Pillar" size="small" select value={filterPillar} onChange={(e) => setFilterPillar(e.target.value)} sx={{ minWidth: 200 }}>
                    <MenuItem value="">All</MenuItem>
                    {ROADMAP_PILLARS.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                            {p.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField label="Status" size="small" select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 150 }}>
                    <MenuItem value="">All</MenuItem>
                    {STATUSES.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField label="Risk" size="small" select value={filterRisk} onChange={(e) => setFilterRisk(e.target.value)} sx={{ minWidth: 130 }}>
                    <MenuItem value="">All</MenuItem>
                    {RISK_LEVELS.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                            {r.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField label="Health" size="small" select value={filterHealth} onChange={(e) => setFilterHealth(e.target.value)} sx={{ minWidth: 130 }}>
                    <MenuItem value="">All</MenuItem>
                    {HEALTH_STATES.map((h) => (
                        <MenuItem key={h.id} value={h.id}>
                            {h.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                <Paper sx={{ p: 2, flex: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <FlagIcon color="primary" />
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            {stats.total}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            In scope
                        </Typography>
                    </Box>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <TrendingUpIcon color="primary" />
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            {stats.inProgress}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            In progress
                        </Typography>
                    </Box>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CheckCircleOutlineIcon color="success" />
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            {stats.achieved}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Achieved
                        </Typography>
                    </Box>
                </Paper>
                <Paper
                    sx={{
                        p: 2,
                        flex: 1.5,
                        borderColor: stats.blockedCount || stats.overdueCount ? "warning.main" : "divider",
                        borderWidth: stats.blockedCount ? 1 : 0,
                        borderStyle: "solid",
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <HourglassEmptyIcon color={stats.next ? "primary" : "disabled"} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={700} noWrap>
                                {stats.next ? stats.next.title : "No next target"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {stats.next ? `${stats.next.target_date} · ${horizonLabel(stats.next.target_date)}` : "Add dated milestones"}
                                {stats.blockedCount ? ` · ${stats.blockedCount} blocked` : ""}
                                {stats.watchCount ? ` · ${stats.watchCount} on watch` : ""}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </Stack>

            <Paper sx={{ px: 1, border: 1, borderColor: "divider" }}>
                <Tabs
                    value={view}
                    onChange={(_, v) => setView(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ minHeight: 48, "& .MuiTab-root": { minHeight: 48, textTransform: "none", fontWeight: 600 } }}
                >
                    <Tab value="timeline" icon={<ViewTimelineIcon />} iconPosition="start" label="Timeline" />
                    <Tab value="year" icon={<CalendarViewMonthIcon />} iconPosition="start" label="Year plan" />
                    <Tab value="table" icon={<TableChartIcon />} iconPosition="start" label="Table" />
                    <Tab value="insights" icon={<InsightsIcon />} iconPosition="start" label="Insights" />
                </Tabs>
            </Paper>

            <TabPanel value={view} index="timeline">
                {displayed.length === 0 ? (
                    <Paper sx={{ p: 6, textAlign: "center", mt: 2 }}>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                            No items match filters. Clear year or search, or add milestones.
                        </Typography>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={openAdd}>
                            Add milestone
                        </Button>
                    </Paper>
                ) : (
                    <Stack spacing={0} sx={{ mt: 1 }}>
                        {displayed.map((item, index) => (
                            <Stack key={item.id} direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "stretch" }} sx={{ py: 2 }}>
                                <Box sx={{ width: { xs: "100%", sm: 128 }, flexShrink: 0, textAlign: { xs: "left", sm: "right" }, pr: { sm: 1 } }}>
                                    <Typography variant="subtitle2" color="primary" fontWeight={800}>
                                        {item.target_date}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {horizonLabel(item.target_date)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {quarterLabel(quarterFromDate(item.target_date))} {yearFromDate(item.target_date)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", width: 24, flexShrink: 0 }}>
                                    <Box
                                        sx={(theme) => ({
                                            width: 14,
                                            height: 14,
                                            borderRadius: "50%",
                                            bgcolor: "primary.main",
                                            border: "3px solid",
                                            borderColor: "background.paper",
                                            boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}`,
                                            zIndex: 1,
                                            mt: 0.5,
                                        })}
                                    />
                                    {index < displayed.length - 1 ? (
                                        <Box
                                            sx={(theme) => ({
                                                flex: 1,
                                                width: 2,
                                                minHeight: 32,
                                                bgcolor: alpha(theme.palette.primary.main, 0.35),
                                                borderRadius: 1,
                                                mt: 0.5,
                                            })}
                                        />
                                    ) : null}
                                </Box>
                                <Card sx={{ flex: 1, border: 1, borderColor: "divider", borderLeftWidth: { xs: 3, sm: 1 }, borderLeftColor: { xs: "primary.main", sm: "divider" } }}>
                                    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
                                                    {item.title}
                                                </Typography>
                                                {item.owner ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Owner: {item.owner}
                                                    </Typography>
                                                ) : null}
                                                {item.description ? (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                                                        {item.description}
                                                    </Typography>
                                                ) : null}
                                                {item.key_metric ? (
                                                    <Chip size="small" label={item.key_metric} sx={{ mt: 1 }} variant="outlined" />
                                                ) : null}
                                                <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, Number(item.progress_percent) || 0))} sx={{ mt: 1.5, maxWidth: 280, height: 6, borderRadius: 1 }} />
                                            </Box>
                                            <Stack direction="row" spacing={0.5} flexShrink={0}>
                                                <Tooltip title="Duplicate">
                                                    <IconButton size="small" onClick={() => duplicateItem(item)}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <IconButton size="small" aria-label="Edit" onClick={() => openEdit(item)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" aria-label="Delete" color="error" onClick={() => remove(item.id)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Stack>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                                            <Chip size="small" label={pillarLabel(item.pillar)} variant="outlined" />
                                            <Chip size="small" label={STATUSES.find((s) => s.id === item.status)?.label || item.status} color={statusChipColor(item.status)} variant={item.status === "planned" ? "outlined" : "filled"} />
                                            <Chip size="small" label={EFFORT_BANDS.find((e) => e.id === item.effort_band)?.label || item.effort_band} variant="outlined" />
                                            <Chip size="small" label={RISK_LEVELS.find((r) => r.id === item.risk_level)?.label || item.risk_level} color={item.risk_level === "high" ? "error" : item.risk_level === "medium" ? "warning" : "default"} variant="outlined" />
                                            <Chip size="small" label={HEALTH_STATES.find((h) => h.id === item.health)?.label || item.health} color={healthChipColor(item.health)} variant="filled" />
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </TabPanel>

            <TabPanel value={view} index="year">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Quarter columns use the <strong>Year focus</strong> filter above, or the current calendar year when it is set to “All years”.
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2" color="text.secondary">
                        Quick year
                    </Typography>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={filterYear || String(new Date().getFullYear())}
                        onChange={(_, v) => v && setFilterYear(String(v))}
                    >
                        {yearOptions.map((y) => (
                            <ToggleButton key={y} value={String(y)}>
                                {y}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    {!filterYear && (
                        <Button size="small" onClick={() => setFilterYear(String(new Date().getFullYear()))}>
                            Focus current year
                        </Button>
                    )}
                </Stack>
                <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((q) => (
                        <Grid item xs={12} md={6} lg={3} key={q}>
                            <Paper sx={{ p: 2, height: "100%", minHeight: 280, border: 1, borderColor: "divider" }}>
                                <Typography variant="subtitle1" fontWeight={800} color="primary" sx={{ mb: 1.5 }}>
                                    {quarterLabel(q)} {filterYear || new Date().getFullYear()}
                                </Typography>
                                <Stack spacing={1}>
                                    {(quarterBuckets[q] || []).length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No milestones
                                        </Typography>
                                    ) : (
                                        quarterBuckets[q].map((item) => (
                                            <Paper key={item.id} variant="outlined" sx={{ p: 1.25, cursor: "pointer" }} onClick={() => openEdit(item)}>
                                                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                                                    {item.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {item.target_date}
                                                </Typography>
                                                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }} useFlexGap>
                                                    <Chip size="small" label={pillarLabel(item.pillar)} />
                                                    <Chip size="small" label={STATUSES.find((s) => s.id === item.status)?.label} color={statusChipColor(item.status)} />
                                                </Stack>
                                            </Paper>
                                        ))
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </TabPanel>

            <TabPanel value={view} index="table">
                <TableContainer component={Paper} sx={{ border: 1, borderColor: "divider" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sortDirection={tableOrderBy === "title" ? tableOrder : false}>
                                    <TableSortLabel active={tableOrderBy === "title"} direction={tableOrderBy === "title" ? tableOrder : "asc"} onClick={() => handleTableSort("title")}>
                                        Title
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={tableOrderBy === "target_date" ? tableOrder : false}>
                                    <TableSortLabel active={tableOrderBy === "target_date"} direction={tableOrderBy === "target_date" ? tableOrder : "asc"} onClick={() => handleTableSort("target_date")}>
                                        Target
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Pillar</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Owner</TableCell>
                                <TableCell>Metric</TableCell>
                                <TableCell align="right">Budget</TableCell>
                                <TableCell>Risk</TableCell>
                                <TableCell>Health</TableCell>
                                <TableCell sortDirection={tableOrderBy === "progress_percent" ? tableOrder : false}>
                                    <TableSortLabel active={tableOrderBy === "progress_percent"} direction={tableOrderBy === "progress_percent" ? tableOrder : "asc"} onClick={() => handleTableSort("progress_percent")}>
                                        %
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedTableRows.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell sx={{ fontWeight: 600, maxWidth: 220 }}>{row.title}</TableCell>
                                    <TableCell>{row.target_date}</TableCell>
                                    <TableCell>{pillarLabel(row.pillar)}</TableCell>
                                    <TableCell>{STATUSES.find((s) => s.id === row.status)?.label}</TableCell>
                                    <TableCell>{row.owner || "—"}</TableCell>
                                    <TableCell sx={{ maxWidth: 140 }}>{row.key_metric || "—"}</TableCell>
                                    <TableCell align="right">{row.budget_estimate != null ? Number(row.budget_estimate).toLocaleString() : "—"}</TableCell>
                                    <TableCell>{RISK_LEVELS.find((r) => r.id === row.risk_level)?.label}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={HEALTH_STATES.find((h) => h.id === row.health)?.label} color={healthChipColor(row.health)} />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 100 }}>
                                        <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, Number(row.progress_percent) || 0))} sx={{ height: 8, borderRadius: 1 }} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => openEdit(row)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => remove(row.id)}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TabPanel>

            <TabPanel value={view} index="insights">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
                            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
                                Active work by pillar
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                Share of non-achieved, non-deferred items — use to spot imbalance.
                            </Typography>
                            {pillarMix.map((p) => (
                                <Box key={p.id} sx={{ mb: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                        <Typography variant="body2">{p.label}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {p.count} ({p.pct}%)
                                        </Typography>
                                    </Stack>
                                    <LinearProgress variant="determinate" value={p.pct} sx={{ height: 8, borderRadius: 1 }} />
                                </Box>
                            ))}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <WarningAmberIcon color="warning" />
                                <Typography variant="subtitle1" fontWeight={800}>
                                    Needs attention
                                </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                Blocked items, or overdue with watch / high risk.
                            </Typography>
                            {attentionItems.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Nothing flagged. Keep reviewing health on in-flight work.
                                </Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {attentionItems.map((item) => (
                                        <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="body2" fontWeight={700}>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.target_date} · {item.health} · risk {item.risk_level}
                                            </Typography>
                                            <Button size="small" sx={{ mt: 1 }} onClick={() => openEdit(item)}>
                                                Review
                                            </Button>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
                            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                                Planning habits
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
                                <li>Reconcile roadmap with calendar and tasks weekly.</li>
                                <li>Move achieved items promptly so forecasts stay honest.</li>
                                <li>Use “blocked” when waiting on a dependency — note it in Dependencies.</li>
                                <li>Set a review date for strategic bets so they don’t drift.</li>
                                <li>Export CSV before leadership meetings for a stable snapshot.</li>
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </TabPanel>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{form.id ? "Edit milestone" : "New milestone"}</DialogTitle>
                <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Tab label="Core" />
                    <Tab label="Metrics & ownership" />
                    <Tab label="Risk & tracking" />
                </Tabs>
                <DialogContent>
                    {dialogTab === 0 && (
                        <Stack spacing={2} sx={{ pt: 2 }}>
                            <TextField label="Title" fullWidth required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Outcome-oriented title" />
                            <TextField label="Description" fullWidth multiline rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            <TextField label="Target date" fullWidth type="date" InputLabelProps={{ shrink: true }} value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
                            <TextField label="Strategic pillar" fullWidth select value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value })}>
                                {ROADMAP_PILLARS.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField label="Status" fullWidth select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                {STATUSES.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField label="Sort order (same-day ordering)" fullWidth type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
                            <TextField label="Success criteria" fullWidth multiline rows={2} value={form.success_criteria} onChange={(e) => setForm({ ...form, success_criteria: e.target.value })} placeholder="How you’ll know this is done" />
                        </Stack>
                    )}
                    {dialogTab === 1 && (
                        <Stack spacing={2} sx={{ pt: 2 }}>
                            <TextField label="Owner / DRI" fullWidth value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Name or team" />
                            <TextField label="Key metric (KPI or headline number)" fullWidth value={form.key_metric} onChange={(e) => setForm({ ...form, key_metric: e.target.value })} placeholder="e.g. ARR $420K, NPS 45, 12 enterprise pilots" />
                            <TextField label="Budget estimate (optional)" fullWidth type="number" value={form.budget_estimate} onChange={(e) => setForm({ ...form, budget_estimate: e.target.value })} inputProps={{ min: 0, step: "any" }} />
                            <TextField label="Effort size" fullWidth select value={form.effort_band} onChange={(e) => setForm({ ...form, effort_band: e.target.value })}>
                                {EFFORT_BANDS.map((e) => (
                                    <MenuItem key={e.id} value={e.id}>
                                        {e.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={[]}
                                value={tagValues}
                                onChange={(_, v) => setForm({ ...form, tags: formatTags(v) })}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip variant="outlined" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
                                    ))
                                }
                                renderInput={(params) => <TextField {...params} label="Tags" placeholder="Type and press Enter" helperText="Organize initiatives (e.g. Q3, enterprise, infra)" />}
                            />
                            <TextField label="Next review date" fullWidth type="date" InputLabelProps={{ shrink: true }} value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} />
                        </Stack>
                    )}
                    {dialogTab === 2 && (
                        <Stack spacing={2} sx={{ pt: 2 }}>
                            <TextField label="Risk level" fullWidth select value={form.risk_level} onChange={(e) => setForm({ ...form, risk_level: e.target.value })}>
                                {RISK_LEVELS.map((r) => (
                                    <MenuItem key={r.id} value={r.id}>
                                        {r.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField label="Health" fullWidth select value={form.health} onChange={(e) => setForm({ ...form, health: e.target.value })}>
                                {HEALTH_STATES.map((h) => (
                                    <MenuItem key={h.id} value={h.id}>
                                        {h.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Progress {form.progress_percent}%
                                </Typography>
                                <Slider value={Number(form.progress_percent) || 0} onChange={(_, v) => setForm({ ...form, progress_percent: v })} valueLabelDisplay="auto" step={5} marks min={0} max={100} />
                            </Box>
                            <TextField label="Dependencies / blockers" fullWidth multiline rows={2} value={form.dependency_note} onChange={(e) => setForm({ ...form, dependency_note: e.target.value })} placeholder="Waiting on vendor, hire, legal, …" />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={save}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Roadmap;
